import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef, useMemo } from 'react';

interface AdBannerContextType {
  isBannerVisible: boolean;
  setIsBannerVisible: (visible: boolean) => void;
  hasBanner: boolean;
  setHasBanner: (has: boolean) => void;
  closeBanner: () => void;
  reopenBanner: () => void;
  activeBannerIds: string[];
  setActiveBannerIds: (ids: string[]) => void;
}

const AdBannerContext = createContext<AdBannerContextType | undefined>(undefined);

const STORAGE_KEY = 'energio_ad_banner_closed';

// Helper to check if banner should be visible based on localStorage
const shouldShowBanner = (currentBannerIds: string[]): boolean => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    
    if (!stored) {

      return true;
    }

    const { closedAt, bannerIds } = JSON.parse(stored);
    const closedTime = new Date(closedAt);
    const now = new Date();
    
    // Check if 24 hours have passed
    const hoursPassed = (now.getTime() - closedTime.getTime()) / (1000 * 60 * 60);
    
    if (hoursPassed >= 24) {
      localStorage.removeItem(STORAGE_KEY);
      return true;
    }

    // Check if banner IDs have changed (new banners from GraphQL)
    const storedIds = bannerIds.sort().join(',');
    const currentIds = currentBannerIds.sort().join(',');
    
    if (storedIds !== currentIds) {
      localStorage.removeItem(STORAGE_KEY);
      return true;
    }

    return false;
  } catch (error) {
    return true;
  }
};

export const AdBannerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [hasBanner, setHasBanner] = useState(false);
  const [activeBannerIds, setActiveBannerIds] = useState<string[]>([]);
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const [prevIdsString, setPrevIdsString] = useState<string>('');
  const [manuallyOpened, setManuallyOpened] = useState(false);
  // refs to track scheduled deferred work so we can cancel on updates/unmount
  const idleHandleRef = useRef<number | null>(null);
  const rafHandleRef = useRef<number | null>(null);
  const timeoutHandleRef = useRef<number | null>(null);

  // Cancel any scheduled deferred tasks (idle/raf/timeout)
  const cancelScheduled = useCallback(() => {
    const cancelIdleCb = (window as any).cancelIdleCallback;
    if (idleHandleRef.current != null && cancelIdleCb) {
      try { cancelIdleCb(idleHandleRef.current); } catch (e) { /* ignore */ }
      idleHandleRef.current = null;
    }
    if (rafHandleRef.current != null) {
      try { cancelAnimationFrame(rafHandleRef.current); } catch (e) { /* ignore */ }
      rafHandleRef.current = null;
    }
    if (timeoutHandleRef.current != null) {
      try { clearTimeout(timeoutHandleRef.current); } catch (e) { /* ignore */ }
      timeoutHandleRef.current = null;
    }
  }, []);

  // Check localStorage when active banner IDs change
  // Use a string comparison to avoid infinite loops from array reference changes
  useEffect(() => {
    const idsString = activeBannerIds.sort().join(',');
    
    
    // Only check if IDs actually changed and banner wasn't manually opened
    if (idsString !== prevIdsString && !manuallyOpened) {
      setPrevIdsString(idsString);

      if (activeBannerIds.length > 0) {
        const shouldShow = shouldShowBanner(activeBannerIds);

        // Defer showing the banner to avoid synchronous DOM mutations that
        // third-party vendor scripts/iframes might perform during navigation
        // or first paint. Use requestIdleCallback when available, otherwise
        // fallback to an rAF + timeout.
  const idleCb = (window as any).requestIdleCallback;

        // If we shouldn't show, cancel any scheduled show and hide immediately
        if (!shouldShow) {
          cancelScheduled();
          setIsBannerVisible(false);
        } else {
          // schedule a deferred show
          cancelScheduled();
          if (idleCb) {
            idleHandleRef.current = idleCb(() => {
              setIsBannerVisible(true);
              idleHandleRef.current = null;
            }, { timeout: 500 });
          } else {
            rafHandleRef.current = requestAnimationFrame(() => {
              timeoutHandleRef.current = window.setTimeout(() => {
                setIsBannerVisible(true);
                timeoutHandleRef.current = null;
              }, 300);
              rafHandleRef.current = null;
            });
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBannerIds, manuallyOpened]);

  // cleanup scheduled tasks on unmount
  useEffect(() => {
    return () => {
      cancelScheduled();
    };
  }, [cancelScheduled]);

  const closeBanner = useCallback(() => {
    try {
      setManuallyOpened(false); // Reset manual flag when closing
      const data = {
        closedAt: new Date().toISOString(),
        bannerIds: activeBannerIds
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setIsBannerVisible(false);
    } catch (error) {
      setIsBannerVisible(false);
    }
  }, [activeBannerIds]);

  const reopenBanner = useCallback(() => {
    setManuallyOpened(true); // Mark as manually opened
    localStorage.removeItem(STORAGE_KEY);
    setIsBannerVisible(true);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    isBannerVisible,
    setIsBannerVisible,
    hasBanner,
    setHasBanner,
    closeBanner,
    reopenBanner,
    activeBannerIds,
    setActiveBannerIds
  }), [isBannerVisible, hasBanner, closeBanner, reopenBanner, activeBannerIds]);

  return (
    <AdBannerContext.Provider value={contextValue}>
      {children}
    </AdBannerContext.Provider>
  );
};

export const useAdBanner = () => {
  const context = useContext(AdBannerContext);
  if (context === undefined) {
    throw new Error('useAdBanner must be used within an AdBannerProvider');
  }
  return context;
};
